// Resume Layout QA — CHARACTER COUNT system. Plain .length, hard ranges,
// exactly verifiable on both ends.
//
// Marcus's specification:
//   Career highlight  200–210 characters total (bold title + ": " + description)
//   PE bullet          90–100 characters
// A line outside its range gets rewritten to land INSIDE the range —
// shortened if over, expanded (never with invented facts) if under.

export const HIGHLIGHT_MIN_CHARS = 200;
export const HIGHLIGHT_MAX_CHARS = 210;
export const PE_BULLET_MIN_CHARS = 95;  // Marcus 2026-09: 95-100 so bullets fill the line nearly edge to edge
export const PE_BULLET_MAX_CHARS = 100; // hard ceiling — ~100 chars fit one rendered line

// Career Summary runt-line control. The summary is JUSTIFIED in the template,
// so every full line stretches edge to edge; a short leftover last line (a
// "runt") looks broken. Line breaks are SIMULATED with a canvas word-wrap in
// the template's font (proportional — "W" is ~3x wider than "i"), anchored to
// the observed calibration that a full justified line averages ~112 characters
// of typical summary text. Plain modulo arithmetic missed real breaks.
export const SUMMARY_CHARS_PER_LINE = 112; // calibration anchor from a real render
export const SUMMARY_MIN_FILL = 0.72;      // last line below 72% of the column = flagged
export const SUMMARY_TARGET_LO = 0.78;     // lengthen rewrites aim to land 78–97%
export const SUMMARY_TARGET_HI = 0.97;

const SUMMARY_FONT = '13.3px Calibri, "Segoe UI", Arial, sans-serif';
let sumCtx: CanvasRenderingContext2D | null = null;
function sumMeasure(text: string): number {
  if (!sumCtx) sumCtx = document.createElement("canvas").getContext("2d")!;
  sumCtx.font = SUMMARY_FONT;
  return sumCtx.measureText(text).width;
}

export interface SummaryWrap {
  lines: number;   // simulated rendered line count
  lastW: number;   // px width of the simulated last line
  usable: number;  // px width of one full column line
  avgCW: number;   // average char width of THIS text (self-calibrating)
  frac: number;    // lastW / usable — how "full" the last line reads
}

/** Greedy word-wrap simulation — same break points Word uses for justified text. */
export function measureSummaryWrap(text: string): SummaryWrap {
  const avgCW = sumMeasure(text) / Math.max(1, text.length);
  const usable = avgCW * SUMMARY_CHARS_PER_LINE;
  const spaceW = sumMeasure(" ");
  let lines = 1, lineW = 0;
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const w = sumMeasure(word);
    if (lineW > 0 && lineW + spaceW + w > usable) { lines++; lineW = w; }
    else lineW += (lineW > 0 ? spaceW : 0) + w;
  }
  return { lines, lastW: lineW, usable, avgCW, frac: lineW / usable };
}

export interface QaIssue {
  id: string;             // "h3", "e2.4", or "s" (summary) — same scheme as resumeFit applyFits
  label: string;
  kind: "highlight" | "pe_bullet" | "summary";
  text: string;           // rewritable text (highlight DESCRIPTION only, full bullet, or full summary)
  current_chars: number;  // character count of the full element right now
  min_chars: number;      // range for the REWRITABLE portion
  max_chars: number;
  direction: "shorten" | "lengthen";
}

interface HighlightJson { chtitle?: string; chdescription?: string }
interface ExperienceJson { company?: string; Descriptions?: Array<{ Description?: string }> }
export interface FlatResume {
  Summary?: string;
  CareerHighlights?: HighlightJson[];
  ProfessionalExperience?: ExperienceJson[];
}

/** Every highlight outside 200–210 total chars, every bullet outside 90–100,
 *  and a career summary whose justified last line is a runt. */
export function findQaIssues(flat: FlatResume): QaIssue[] {
  const out: QaIssue[] = [];

  // Career Summary runt detection: simulate the real wrap, flag any last line
  // under SUMMARY_MIN_FILL of the column — Marcus wants the justified block
  // even, not merely runt-free.
  const summary = (flat.Summary ?? "").trim();
  if (summary) {
    const m = measureSummaryWrap(summary);
    if (m.lines >= 2 && m.frac < SUMMARY_MIN_FILL) {
      // Nearest fix in characters: trim the runt up into the previous line, or
      // grow the last line until it lands in the 78–97% "reads as full" band.
      const runtChars = Math.ceil(m.lastW / m.avgCW) + 2;                              // chars to remove
      const addMin = Math.max(1, Math.ceil(((SUMMARY_TARGET_LO - m.frac) * m.usable) / m.avgCW));
      const addMax = Math.max(addMin + 8, Math.floor(((SUMMARY_TARGET_HI - m.frac) * m.usable) / m.avgCW));
      const shorten = runtChars <= addMin;
      out.push({
        id: "s",
        label: "Career Summary (runt last line)",
        kind: "summary",
        text: summary,
        current_chars: summary.length,
        min_chars: shorten ? Math.max(12, summary.length - runtChars - 15) : summary.length + addMin,
        max_chars: shorten ? summary.length - runtChars : summary.length + addMax,
        direction: shorten ? "shorten" : "lengthen",
      });
    }
  }

  (flat.CareerHighlights ?? []).forEach((h, i) => {
    const title = (h.chtitle ?? "").trim();
    const desc = (h.chdescription ?? "").trim();
    if (!desc) return;
    const prefix = title.length + 2; // "Title: "
    const total = prefix + desc.length;
    if (total >= HIGHLIGHT_MIN_CHARS && total <= HIGHLIGHT_MAX_CHARS) return;
    out.push({
      id: `h${i}`, label: `Highlight #${i + 1} (${title})`, kind: "highlight", text: desc,
      current_chars: total,
      min_chars: HIGHLIGHT_MIN_CHARS - prefix,
      max_chars: HIGHLIGHT_MAX_CHARS - prefix,
      direction: total > HIGHLIGHT_MAX_CHARS ? "shorten" : "lengthen",
    });
  });

  (flat.ProfessionalExperience ?? []).forEach((j, ji) => {
    (j.Descriptions ?? []).forEach((b, bi) => {
      const t = (b.Description ?? "").trim();
      if (!t) return;
      if (t.length >= PE_BULLET_MIN_CHARS && t.length <= PE_BULLET_MAX_CHARS) return;
      out.push({
        id: `e${ji}.${bi}`, label: `${j.company ?? "Job"} bullet ${bi + 1}`, kind: "pe_bullet", text: t,
        current_chars: t.length,
        min_chars: PE_BULLET_MIN_CHARS,
        max_chars: PE_BULLET_MAX_CHARS,
        direction: t.length > PE_BULLET_MAX_CHARS ? "shorten" : "lengthen",
      });
    });
  });

  return out;
}

/**
 * Width-balance skill rows. Skills stay in their EXACT order (JD order is a
 * hard rule) — only the row BREAKS move, like a typesetter evening out lines.
 * Widths are measured on canvas in the template font, so a single long skill
 * ("Technical Proficiency with Verizon Systems (MTAS, ETNI, and RSSx)") no
 * longer produces one row far wider than its neighbors. Max 4 per row (the
 * Docmosis SkillRows schema); row count may change.
 */
export function balanceSkillRows(
  rows: Array<Record<string, string>> | undefined,
  prefix: string,
): { rows: Array<Record<string, string>>; changed: boolean } {
  const orig = rows ?? [];
  const items: string[] = [];
  const origBreaks: number[] = [];
  for (const r of orig) {
    let count = 0;
    for (let i = 1; i <= 4; i++) {
      const v = (r[`${prefix}${i}`] ?? "").trim();
      if (v) { items.push(v); count++; }
    }
    if (count) origBreaks.push(count);
  }
  if (items.length < 3) return { rows: orig, changed: false };

  const w = items.map((s) => sumMeasure(s));
  const sepW = sumMeasure(" • ");
  const rowWidth = (a: number, b: number) => { // items a..b-1
    let sum = 0;
    for (let k = a; k < b; k++) sum += w[k];
    return sum + sepW * (b - a - 1);
  };

  // Marcus's rule: pack as many skills per line as possible FIRST, then even
  // the widths. So: find the SMALLEST row count that fits (≤4 per row AND no
  // row wider than the drawable line), and only balance within that count.
  // The line cap comes from the same calibration the summary uses.
  const n = items.length;
  const totalItemW = w.reduce((x, y) => x + y, 0);
  const avgCW = sumMeasure(items.join(" ")) / Math.max(1, items.join(" ").length);
  const lineCap = Math.max(avgCW * SUMMARY_CHARS_PER_LINE, Math.max(...w) + 1);
  const fitsCap = (a: number, b: number) => rowWidth(a, b) <= lineCap;
  // Feasibility scan: greedy fill to find the minimum viable row count.
  let minR = 0;
  { let i = 0;
    while (i < n) {
      let j = i + 1;
      while (j < n && j - i < 4 && fitsCap(i, j + 1)) j++;
      minR++; i = j;
    }
  }
  minR = Math.max(minR, Math.ceil(n / 4));
  // Evaluate the tightest packing AND one extra row. The extra row is taken
  // ONLY when it cuts the width unevenness by more than half — e.g. 20 skills
  // pack as exactly 5×4 with zero freedom, and the only way to get "Microsoft
  // Excel onto the last row" is a 6th row. Pure packing stays the default.
  const candidates: Array<{ R: number; cost: number; breaks: number[] }> = [];
  let best: { cost: number; breaks: number[] } | null = null;
  for (let R = minR; R <= Math.min(n, minR + 1); R++) {
    const totalW = w.reduce((x, y) => x + y, 0) + sepW * (n - R);
    const target = totalW / R;
    // dp[i][r] = best cost splitting first i items into r rows
    const INF = Number.POSITIVE_INFINITY;
    const dp: number[][] = Array.from({ length: n + 1 }, () => Array(R + 1).fill(INF));
    const from: number[][] = Array.from({ length: n + 1 }, () => Array(R + 1).fill(-1));
    dp[0][0] = 0;
    for (let i = 1; i <= n; i++) {
      for (let r = 1; r <= Math.min(i, R); r++) {
        for (let k = Math.max(r - 1, i - 4); k < i; k++) {
          if (dp[k][r - 1] === INF) continue;
          const rw = rowWidth(k, i);
          if (rw > lineCap) continue; // a row must never exceed the drawable line
          const c = dp[k][r - 1] + Math.pow(rw - target, 2);
          if (c < dp[i][r]) { dp[i][r] = c; from[i][r] = k; }
        }
      }
    }
    if (dp[n][R] === INF) continue;
    const breaks: number[] = [];
    let i = n, r = R;
    while (r > 0) { const k = from[i][r]; breaks.unshift(i - k); i = k; r--; }
    candidates.push({ R, cost: dp[n][R], breaks });
  }
  if (candidates.length === 0) return { rows: orig, changed: false };
  // Tightest packing wins unless one more row knocks down the WIDEST row by
  // 15%+ — the visual offense is a single row towering over its neighbors
  // (e.g. 418/474/487/630/410: the 630 is the problem, and only an extra row
  // can relieve it when every row is already at 4 skills).
  const maxRowOf = (breaks: number[]): number => {
    let idx = 0, maxW = 0;
    for (const count of breaks) { maxW = Math.max(maxW, rowWidth(idx, idx + count)); idx += count; }
    return maxW;
  };
  best = candidates[0];
  for (const cand of candidates.slice(1)) {
    if (maxRowOf(cand.breaks) < maxRowOf(best.breaks) * 0.85) best = cand;
  }

  const changed = JSON.stringify(best.breaks) !== JSON.stringify(origBreaks);
  if (!changed) return { rows: orig, changed: false };
  const out: Array<Record<string, string>> = [];
  let idx = 0;
  for (const count of best.breaks) {
    const row: Record<string, string> = {};
    for (let i = 1; i <= 4; i++) {
      row[`${prefix}${i}`] = i <= count ? items[idx + i - 1] : "";
    }
    idx += count;
    out.push(row);
  }
  return { rows: out, changed: true };
}

/** Verify a proposed rewrite: exact character count inside the range. */
export function verifyFix(issue: QaIssue, newText: string): { ok: boolean; chars: number; min: number; max: number } {
  const chars = newText.trim().length;
  return { ok: chars >= issue.min_chars && chars <= issue.max_chars, chars, min: issue.min_chars, max: issue.max_chars };
}
